/**
 * SortPanel Component
 * Config-driven Popover for single-field sort with direction toggle.
 * All state is owned by the parent.
 */

import { useRef, useState } from 'react';
import {
  Popover,
  Button,
  Group,
  Text,
  Stack,
  Select,
  ActionIcon,
  Box,
  UnstyledButton,
  Divider,
  type MantineSize,
} from '@mantine/core';
import { ArrowUp, ArrowDown, X, Plus, ChevronDown } from 'react-feather';
import type { SortState } from '../../../utils/sortTasks';
import classes from './SortPanel.module.css';

// ── Types ──────────────────────────────────────────────────────────────────

export interface SortFieldConfig {
  value: string;
  label: string;
}

export interface SortPanelProps {
  /** Available sort fields */
  sortFields: SortFieldConfig[];
  /** Currently active sort (null = default/no sort) */
  activeSort: SortState | null;
  /** Default sort state (used to determine if sort is "active" for display) */
  defaultSort?: SortState;
  /** Callback when sort changes */
  onSortChange: (sort: SortState | null) => void;
  /** Mantine size for trigger button and inner controls (default "sm") */
  size?: MantineSize;
}

export function SortPanel({
  sortFields,
  activeSort,
  defaultSort,
  onSortChange,
  size = 'sm',
}: SortPanelProps) {
  const [opened, setOpened] = useState(false);
  // The clicked button unmounts, so keep focus in the panel: Escape then closes it and Tab goes on.
  const dropdownRef = useRef<HTMLDivElement>(null);
  // The clear button unmounts too, so focus goes back to the trigger.
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  // Each open is a new visit: the content remounts, so no list or select stays open from the last one.
  const [visit, setVisit] = useState(0);

  // Sort is "active" (non-default) when it differs from the default
  const isActive = activeSort !== null && (
    !defaultSort ||
    activeSort.field !== defaultSort.field ||
    activeSort.direction !== defaultSort.direction
  );

  const activeLabel = activeSort
    ? sortFields.find((f) => f.value === activeSort.field)?.label || activeSort.field
    : null;

  const handleClear = () => {
    dropdownRef.current?.focus();
    onSortChange(null);
    setAddMenuOpen(false);
  };

  const handleDismiss = () => {
    handleClear();
    setOpened(false);
  };

  const handleFieldChange = (field: string | null) => {
    if (field) {
      onSortChange({ field, direction: activeSort?.direction ?? 'asc' });
    }
  };

  const handleDirectionChange = (direction: string | null) => {
    if (direction && activeSort) {
      onSortChange({ ...activeSort, direction: direction as 'asc' | 'desc' });
    }
  };

  const handleSelectField = (field: string) => {
    dropdownRef.current?.focus();
    onSortChange({ field, direction: 'asc' });
    setAddMenuOpen(false);
  };

  return (
    <Box pos="relative" display="inline-flex">
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal
      shadow="md"
      trapFocus={false}
    >
      <Popover.Target>
        <Button
          ref={triggerRef}
          variant="outline"
          color="gray"
          bg="white"
          style={{ borderColor: '#E8E6E3' }}
          size={size}
          // Space for the clear button, which sits beside the trigger so no button is inside a button
          rightSection={isActive ? <Box w={22} /> : undefined}
          onClick={() => {
            // The add list belongs to one visit: the panel opens with it closed.
            setAddMenuOpen(false);
            if (!opened) setVisit((v) => v + 1);
            setOpened((o) => !o);
          }}
        >
          {activeSort ? `Sort: ${activeLabel}` : 'Sort'}
        </Button>
      </Popover.Target>

      <Popover.Dropdown ref={dropdownRef} tabIndex={-1} className={classes.dropdown}>
        <Stack gap="sm" key={visit}>
          {/* Header */}
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              Sorts
            </Text>
            {activeSort && (
              <Text
                size="sm"
                c="dimmed"
                style={{ cursor: 'pointer' }}
                onClick={handleClear}
              >
                Clear
              </Text>
            )}
          </Group>

          {activeSort ? (
            /* Active sort row */
            <Group gap="xs" wrap="nowrap">
              <Select
                data={sortFields}
                value={activeSort.field}
                onChange={handleFieldChange}
                size={size}
                className={classes.sortFieldSelect}
                comboboxProps={{ withinPortal: false }}
              />
              <Select
                data={[
                  { value: 'asc', label: 'Ascending' },
                  { value: 'desc', label: 'Descending' },
                ]}
                value={activeSort.direction}
                onChange={handleDirectionChange}
                size={size}
                w={140}
                leftSection={
                  activeSort.direction === 'asc' ? (
                    <ArrowUp size={14} />
                  ) : (
                    <ArrowDown size={14} />
                  )
                }
                comboboxProps={{ withinPortal: false }}
              />
              <ActionIcon
                variant="subtle"
                color="gray"
                size="md"
                aria-label="Remove sort"
                onClick={handleClear}
              >
                <X size={16} />
              </ActionIcon>
            </Group>
          ) : (
            /* No active sort — inline field list */
            addMenuOpen ? (
              <Stack gap={0}>
                <Divider mb="xs" />
                {sortFields.map((field) => (
                  <UnstyledButton
                    key={field.value}
                    className={classes.addOption}
                    onClick={() => handleSelectField(field.value)}
                  >
                    <Text size="sm">{field.label}</Text>
                  </UnstyledButton>
                ))}
              </Stack>
            ) : (
              <Button
                variant="subtle"
                color="gray"
                size={size}
                leftSection={<Plus size={16} />}
                rightSection={<ChevronDown size={14} />}
                onClick={() => setAddMenuOpen(true)}
              >
                Add sort
              </Button>
            )
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
      {isActive && (
        <ActionIcon
          variant="transparent"
          size="sm"
          color="gray"
          aria-label="Clear sort"
          onClick={() => {
            handleDismiss();
            triggerRef.current?.focus();
          }}
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}
        >
          <X size={14} />
        </ActionIcon>
      )}
    </Box>
  );
}

export default SortPanel;
